import { Box, Chip, LinearProgress, Avatar } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import { isEmpty } from "lodash";
import { useCallback, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";
import Txt from "../typography/Txt";
import AgGridTableWrapper from "../agGrid/AgGridTableWrapper";
import { columnTypesBoolean } from "../../_src_shared/utils/agGrid/columnTypes/columnTypesBoolean";
import { sidebarSetValues } from "../../redux/actions/sidebarActions";

// Custom cell renderers
const CheckboxRenderer = (params) => {
  const handleCheckboxChange = (e) => {
    e.stopPropagation();
    params.node.setDataValue("checked", e.target.checked);
  };

  return (
    <input
      type="checkbox"
      checked={params.value || false}
      onChange={handleCheckboxChange}
      style={{ margin: "8px" }}
    />
  );
};

const AccountRenderer = (params) => {
  const { account_name, account_color } = params.data;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          backgroundColor: account_color || "#ccc",
        }}
      />
      <Txt>{account_name}</Txt>
    </Box>
  );
};

const StatusRenderer = (params) => {
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "new":
        return "success";
      case "in-progress":
        return "warning";
      case "completed":
        return "info";
      default:
        return "default";
    }
  };

  return (
    <Chip
      label={params.value}
      color={getStatusColor(params.value)}
      size="small"
      variant="outlined"
    />
  );
};

const ProgressRenderer = (params) => {
  const progress = params.value || 0;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
      <Txt variant="caption" sx={{ minWidth: "30px" }}>
        {progress}%
      </Txt>
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          flex: 1,
          height: 6,
          borderRadius: 3,
          backgroundColor: "#f0f0f0",
          "& .MuiLinearProgress-bar": {
            borderRadius: 3,
          },
        }}
      />
    </Box>
  );
};

const AssigneeRenderer = (params) => {
  if (!params.value) return null;

  return (
    <Avatar
      sx={{ width: 24, height: 24, fontSize: "0.75rem" }}
      src={params.data.assignee_avatar}
    >
      {params.value?.charAt(0)?.toUpperCase()}
    </Avatar>
  );
};

const LendmarqChecklistTable = ({
  quickFilter,
  rowData = checklistDummyData,
}) => {
  const ref = useRef();
  const dispatch = useDispatch();

  const columnDefs = useMemo(
    () => [
      {
        headerName: "",
        field: "checked",
        width: 50,
        cellRenderer: CheckboxRenderer,
        suppressSizeToFit: true,
        pinned: "left",
        checkboxSelection: false,
      },
      {
        headerName: "Account",
        field: "account_name",
        flex: 1,
        minWidth: 180,
        cellRenderer: AccountRenderer,
        sort: "asc",
      },
      {
        headerName: "Note",
        field: "note",
        flex: 2,
        minWidth: 300,
      },
      {
        headerName: "Closed",
        field: "closed",
        width: 80,
        cellRenderer: (params) =>
          params.value ? (
            <Box sx={{ color: "success.main", textAlign: "center" }}>✓</Box>
          ) : (
            <Box sx={{ color: "grey.400", textAlign: "center" }}>○</Box>
          ),
      },
      {
        headerName: "Status",
        field: "status",
        width: 120,
        cellRenderer: StatusRenderer,
      },
      {
        headerName: "Progress",
        field: "progress",
        width: 140,
        cellRenderer: ProgressRenderer,
      },
      {
        headerName: "Due date",
        field: "due_date",
        width: 100,
        valueFormatter: (params) => {
          if (!params.value) return "";
          const date = new Date(params.value);
          return date.toLocaleDateString("en-US", {
            day: "numeric",
            month: "short",
          });
        },
      },
      {
        headerName: "",
        field: "assignee",
        width: 50,
        cellRenderer: AssigneeRenderer,
        suppressSizeToFit: true,
      },
    ],
    []
  );

  const defaultColDef = useMemo(
    () => ({
      filter: true,
      enableRowGroup: true,
      suppressHeaderFilterButton: true,
      suppressHeaderMenuButton: true,
      resizable: true,
    }),
    []
  );

  const onRowClicked = useCallback(
    (params) => {
      const item = params?.data;
      dispatch(
        sidebarSetValues({
          type: "checklistItem",
          state: item,
        })
      );
    },
    [dispatch]
  );

  const columnTypes = useMemo(() => {
    return {
      ...columnTypesBoolean(),
    };
  }, []);

  const onCellValueChanged = useCallback((params) => {
    // Handle checkbox changes or other cell value updates
    console.log("Cell value changed:", params);
  }, []);

  if (isEmpty(rowData)) {
    return (
      <Box sx={{ p: 2 }}>
        <Txt secondary>No checklist items</Txt>
      </Box>
    );
  }

  return (
    <AgGridTableWrapper simpleTable suppressMinHeightWhenPopulated={rowData}>
      <AgGridReact
        ref={ref}
        rowData={rowData}
        columnDefs={columnDefs}
        quickFilterText={quickFilter}
        defaultColDef={defaultColDef}
        columnTypes={columnTypes}
        onRowClicked={onRowClicked}
        onCellValueChanged={onCellValueChanged}
        allowDragFromColumnsToolPanel={false}
        domLayout={"autoHeight"}
        enableCellTextSelection
        suppressRowClickSelection={true}
        rowHeight={48}
      />
    </AgGridTableWrapper>
  );
};
const checklistDummyData = [
  {
    id: 1,
    checked: false,
    account_name: "Bank of America",
    account_color: "#e74c3c",
    note: "AirTable - Onboarding",
    closed: true,
    status: "New",
    progress: 10,
    due_date: "2024-09-19",
    assignee: "John Doe",
    assignee_avatar: null,
  },
  {
    id: 2,
    checked: false,
    account_name: "IBM",
    account_color: "#27ae60",
    note: "Dunder Mifflin Paper - Raised $300M",
    closed: true,
    status: "New",
    progress: 10,
    due_date: "2024-09-19",
    assignee: "Sarah Davis",
    assignee_avatar: null,
  },
  {
    id: 3,
    checked: true,
    account_name: "Louis Vuitton",
    account_color: "#f39c12",
    note: "FTX - Investment by Sequoia",
    closed: false,
    status: "In-Progress",
    progress: 90,
    due_date: "2024-09-19",
    assignee: "Sarah Davis",
    assignee_avatar: null,
  },
  {
    id: 4,
    checked: true,
    account_name: "The Walt Disney Company",
    account_color: "#f39c12",
    note: "Airtable - Drop in treatment plan usage",
    closed: false,
    status: "In-Progress",
    progress: 70,
    due_date: "2024-09-19",
    assignee: "Mike Johnson",
    assignee_avatar: null,
  },
  {
    id: 5,
    checked: false,
    account_name: "General Electric",
    account_color: "#27ae60",
    note: "SpaceX - Viewing documentation a lot",
    closed: true,
    status: "New",
    progress: 10,
    due_date: "2024-09-19",
    assignee: "Alex Chen",
    assignee_avatar: null,
  },
  {
    id: 6,
    checked: false,
    account_name: "eBay",
    account_color: "#f39c12",
    note: "Monsters Inc. - Multiple support tickets have been opened",
    closed: false,
    status: "In-Progress",
    progress: 30,
    due_date: "2024-09-19",
    assignee: "John Doe",
    assignee_avatar: null,
  },
  {
    id: 7,
    checked: false,
    account_name: "Starbucks",
    account_color: "#f39c12",
    note: "AirTable School - Onboarding",
    closed: false,
    status: "In-Progress",
    progress: 30,
    due_date: "2024-09-19",
    assignee: "Lisa Wang",
    assignee_avatar: null,
  },
  {
    id: 8,
    checked: false,
    account_name: "Johnson & Johnson",
    account_color: "#27ae60",
    note: "Facebook Learning - Increase in testing",
    closed: false,
    status: "New",
    progress: 10,
    due_date: "2024-09-19",
    assignee: "John Doe",
    assignee_avatar: null,
  },
  {
    id: 9,
    checked: false,
    account_name: "The Walt Disney Company",
    account_color: "#f39c12",
    note: "AirTable - multiple support tickets have been opened",
    closed: false,
    status: "In-Progress",
    progress: 30,
    due_date: "2024-09-19",
    assignee: "Alex Chen",
    assignee_avatar: null,
  },
  {
    id: 10,
    checked: false,
    account_name: "McDonald's",
    account_color: "#e74c3c",
    note: "Facebook - Exec appeared on a podcast",
    closed: false,
    status: "New",
    progress: 10,
    due_date: "2024-09-19",
    assignee: "Lisa Wang",
    assignee_avatar: null,
  },
  {
    id: 11,
    checked: false,
    account_name: "MasterCard",
    account_color: "#e74c3c",
    note: "Tesla Motors - New CFO has joined the company",
    closed: false,
    status: "In-Progress",
    progress: 70,
    due_date: "2024-09-19",
    assignee: "John Doe",
    assignee_avatar: null,
  },
];

export default LendmarqChecklistTable;
